import React from "react";
import { Box, Typography, Chip, Paper, Divider } from "@mui/material";
import EventStatusIcon from "./EventStatusIcon";
import MedicationIcon from "@mui/icons-material/Medication";
import ViewListIcon from "@mui/icons-material/ViewList";
import { formatTimeToAmPm, formatDateField } from "../../../../../base/FormatTime";

const EventMedicationRow = ({ med, selectedMonth, daysInMonth }) => {
  const isBlisterPack = med.medicine_type === "Blister Pack";
  const formattedStartDate = formatDateField(med.startDate);

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        borderRadius: 1.5,
        border: "1px solid #E2E8F0",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        bgcolor={isBlisterPack ? "#F0F7FF" : "#FAFBFC"}
        borderLeft={isBlisterPack ? "3px solid #2196f3" : "3px solid #9e9e9e"}
        p={1.25}
        flexWrap="wrap"
        gap={1.25}
      >
        <Box display="flex" alignItems="center" minWidth={240} gap={0.75}>
          {isBlisterPack ? (
            <ViewListIcon sx={{ fontSize: 18 }} color="primary" />
          ) : (
            <MedicationIcon sx={{ fontSize: 18 }} color="action" />
          )}
          <Typography
            variant="body2"
            fontWeight={600}
            fontSize="0.82rem"
            color="#1A1A2E"
          >
            {isBlisterPack ? med.title : med.name}
          </Typography>
          <Chip
            label={isBlisterPack ? "Blister Pack" : "Individual"}
            size="small"
            color={isBlisterPack ? "primary" : "default"}
            variant="outlined"
            sx={{ height: 20, fontSize: "0.65rem", fontWeight: 500 }}
          />
        </Box>

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
          flex={1}
          sx={{
            borderLeft: "1px solid #E2E8F0",
            px: 1.5,
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            fontSize="0.72rem"
            sx={{ minWidth: 70 }}
          >
            <strong>{med.type}</strong>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            fontSize="0.72rem"
            sx={{ minWidth: 110 }}
          >
            From: <strong>{formattedStartDate}</strong>
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            fontSize="0.72rem"
            sx={{ minWidth: 70 }}
          >
            {med.frequency}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            fontSize="0.72rem"
            sx={{ minWidth: 55 }}
          >
            Dose: <strong>{med.dose}</strong>
          </Typography>
        </Box>

        {isBlisterPack && med.medicine_list?.length > 0 && (
          <Box
            display="flex"
            alignItems="center"
            gap={0.75}
            minWidth={200}
            flexWrap="wrap"
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={500}
              fontSize="0.72rem"
            >
              Contains:
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.5}>
              {med.medicine_list.map((medicine) => (
                <Chip
                  key={medicine.id}
                  label={medicine.name}
                  size="small"
                  color="info"
                  sx={{ fontSize: "0.65rem", height: 20, fontWeight: 500 }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Divider sx={{ opacity: 0.6 }} />

      <Box sx={{ width: "100%" }}>
        {med.visits.map((visit, vIndex) => (
          <Box
            key={vIndex}
            display="flex"
            alignItems="center"
            p={0.75}
            bgcolor={vIndex % 2 === 0 ? "transparent" : "#FAFBFC"}
            sx={{ width: "100%" }}
          >
            <Typography
              sx={{
                width: "340px",
                fontWeight: 500,
                fontSize: "0.75rem",
                color: isBlisterPack ? "#1976d2" : "#616161",
                pl: 0.75,
                flexShrink: 0,
              }}
            >
              {visit.label}{" "}
              <Box
                component="span"
                sx={{ color: "#94A3B8", ml: 2, fontSize: "0.7rem" }}
              >
                Due: {formatTimeToAmPm(visit?.time)}
              </Box>
            </Typography>

            <Box
              display="flex"
              flexWrap="nowrap"
              sx={{
                flex: 1,
                justifyContent: "space-between",
                pr: 1,
              }}
            >
              {Array.from({ length: daysInMonth }).map((_, dIndex) => {
                const date = dIndex + 1;
                const fullDate = `${selectedMonth}-${date
                  .toString()
                  .padStart(2, "0")}`;
                const visitData = visit.data?.find(
                  (entry) => entry.date === fullDate
                );

                return (
                  <Box
                    key={dIndex}
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 0,
                    }}
                  >
                    <EventStatusIcon
                      status={visitData?.status}
                      reason={visitData?.reason}
                      time={visitData?.time}
                      formattedDate={visitData?.date}
                      stock_status={visitData?.stock_status}
                      notes={visitData?.notes}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default EventMedicationRow;